import type {
  CustomerDetail,
  CustomerDuplicateCheckRequest,
  CustomerDuplicateCheckResult,
  CustomerExposure,
  CustomerKycRejectRequest,
  CustomerMedia,
  CustomerMediaType,
  CustomerStatusChangeRequest,
  CustomerSummary,
  CustomerVerification,
  CustomerWriteRequest,
  DualControlResult,
  KycStatus,
  CustomerStatus,
  PagedResult,
  VerificationOverride,
  VerifyCustomerRequest,
} from './types'

import { apiClient } from './client'

/**
 * The customer endpoints.
 *
 * Identifiers on the wire are public UUIDv7 strings; a sequential database id never appears in a
 * route here. Money crosses as integer minor units paired with a currency code, and every failure
 * arrives as a problem document whose dotted code is what the screens branch on.
 */

export interface CustomerListQuery {
  /** Free text over name and customer number. Phone matches only on the full number. */
  search?: string
  status?: CustomerStatus
  kycStatus?: KycStatus
  branchId?: string
  page?: number
  pageSize?: number
}

export async function listCustomers(query: CustomerListQuery): Promise<PagedResult<CustomerSummary>> {
  const response = await apiClient.get<PagedResult<CustomerSummary>>('/customers', { params: query })
  return response.data
}

export async function getCustomer(customerId: string): Promise<CustomerDetail> {
  const response = await apiClient.get<CustomerDetail>(`/customers/${encodeURIComponent(customerId)}`)
  return response.data
}

/** Creates the record in `Draft`. It leaves that state only through submit-for-review. */
export async function createCustomer(request: CustomerWriteRequest): Promise<CustomerDetail> {
  const response = await apiClient.post<CustomerDetail>('/customers', request)
  return response.data
}

export async function updateCustomer(customerId: string, request: CustomerWriteRequest): Promise<void> {
  await apiClient.put(`/customers/${encodeURIComponent(customerId)}`, request)
}

/*
 * The four command endpoints below are typed as returning nothing, and their callers refetch the
 * customer afterwards. A KYC decision changes the record's status, its approver, and its exposure
 * together, and the round trip that re-reads all three is cheaper than a screen that trusts a
 * response body to have carried them.
 */

export async function submitCustomerForReview(customerId: string): Promise<void> {
  await apiClient.post(`/customers/${encodeURIComponent(customerId)}/submit-for-review`)
}

/**
 * Dual control twice over: the server refuses an approver who is the person that submitted the
 * record, and the approval itself is countersigned — the answer is an envelope, and
 * `awaitingApproval: true` means a request was stored and the file is UNCHANGED until a second
 * manager countersigns it in the inbox. Throwing this envelope away is how a tester once watched
 * a dialog close over nothing.
 */
export async function approveCustomerKyc(customerId: string): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/customers/${encodeURIComponent(customerId)}/kyc/approve`,
  )
  return response.data
}

export async function rejectCustomerKyc(
  customerId: string,
  request: CustomerKycRejectRequest,
): Promise<void> {
  await apiClient.post(`/customers/${encodeURIComponent(customerId)}/kyc/reject`, request)
}

export async function changeCustomerStatus(
  customerId: string,
  request: CustomerStatusChangeRequest,
): Promise<void> {
  await apiClient.put(`/customers/${encodeURIComponent(customerId)}/status`, request)
}

/** The cross-branch banner on its own, for screens that do not hold the whole customer record. */
export async function getCustomerExposure(customerId: string): Promise<CustomerExposure> {
  const response = await apiClient.get<CustomerExposure>(
    `/customers/${encodeURIComponent(customerId)}/exposure`,
  )
  return response.data
}

/**
 * Asks whether a Ghana Card number or phone number is already held anywhere in the institution.
 * Both are matched against a blind index, so a match is exact rather than approximate.
 */
export async function checkCustomerDuplicate(
  request: CustomerDuplicateCheckRequest,
): Promise<CustomerDuplicateCheckResult> {
  const response = await apiClient.post<CustomerDuplicateCheckResult>('/customers/check-duplicate', request)
  return response.data
}

/* ─── Media: passport photo, signature, thumbprint ─────────────────────────────────────────── */

/** The URL segment each specimen streams under — `photo`, `signature`, `thumbprint`. */
const MEDIA_SEGMENTS: Record<CustomerMediaType, string> = {
  PassportPhoto: 'photo',
  Signature: 'signature',
  Thumbprint: 'thumbprint',
}

/**
 * Streams the current specimen of one kind back as bytes, under `customers.media.view`.
 *
 * `responseType: 'blob'` still rides through the same interceptor that attaches the bearer token
 * to every other request — there is no second, unauthenticated path to a customer's photograph.
 * Turning the blob into something an `<img>` can show is the caller's job, via `URL.createObjectURL`
 * — see `features/customers/media-image.tsx`.
 *
 * A 404 means "nothing of this kind has been captured", which is the ordinary, expected answer for
 * a file onboarded before this existed — not a failure to surface as a generic error. Because the
 * request asks for a blob, a JSON problem-details error body arrives here as a `Blob` too rather
 * than as parsed JSON, so the dotted code (`customers.media_not_found`) is not readable off it; the
 * caller branches on the HTTP status instead. See `useCustomerMedia`.
 */
export async function getCustomerMediaBlob(
  customerId: string,
  mediaType: CustomerMediaType,
): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    `/customers/${encodeURIComponent(customerId)}/${MEDIA_SEGMENTS[mediaType]}`,
    { responseType: 'blob' },
  )
  return response.data
}

/**
 * Captures a passport photograph, signature specimen, or thumbprint specimen, under
 * `customers.media.capture`. A new capture supersedes the previous one; the server keeps history,
 * this client only ever shows the current specimen.
 */
export async function uploadCustomerMedia(
  customerId: string,
  mediaType: CustomerMediaType,
  file: Blob,
): Promise<CustomerMedia> {
  const form = new FormData()
  form.append('file', file)

  const response = await apiClient.post<CustomerMedia>(
    `/customers/${encodeURIComponent(customerId)}/${MEDIA_SEGMENTS[mediaType]}`,
    form,
  )
  return response.data
}

/* ─── Transaction verification ──────────────────────────────────────────────────────────────── */

/**
 * Records that a teller confirmed the person at the counter is the account holder, immediately
 * before posting. Under `teller.verification.perform` — not dual-controlled, unlike almost
 * everything else that gates money moving: a teller does this dozens of times a day, and the
 * oversight for it is the override queue below, not a second signature on every visit.
 */
export async function verifyCustomer(
  customerId: string,
  request: VerifyCustomerRequest,
): Promise<CustomerVerification> {
  const response = await apiClient.post<CustomerVerification>(
    `/customers/${encodeURIComponent(customerId)}/verifications`,
    request,
  )
  return response.data
}

/**
 * The supervisor's queue: every `Override` verification within reach, most recent first, under
 * `teller.verification.review`. `branchId` narrows to one branch; omitted, it is every branch the
 * caller may act for.
 */
export async function listVerificationOverrides(
  branchId?: string,
): Promise<VerificationOverride[]> {
  const response = await apiClient.get<VerificationOverride[]>('/customers/verifications/overrides', {
    params: branchId ? { branchId } : undefined,
  })
  return response.data
}
